// Extracts chartable data from an assistant answer so the panel can offer a
// "View as chart" button. Two sources are tried, in priority order:
//
//   1. A markdown pipe-table (header row + numeric columns).
//   2. "Label - $value metric | $value metric | …" list lines, the shape the
//      model tends to use for "top campaigns by revenue" style answers.
//
// Returns null when nothing chartable is found. Otherwise:
//   { labels: string[], series: [{ name: string, values: number[] }] }
// Only columns/metrics that are numeric for every row become a series, and we
// require at least two rows so a single value never produces a chart.

const MIN_ROWS = 2

// Pull a number out of a cell like "$2,020.00", "-$0.08", "17", "42%".
// Returns null when the text has no usable number.
function toNumber(raw) {
  if (raw == null) {
    return null
  }

  const cleaned = String(raw).replace(/[$,%\s]/g, '').replace(/[()]/g, '')
  if (cleaned === '' || !/\d/.test(cleaned)) {
    return null
  }

  const n = Number(cleaned)
  return Number.isFinite(n) ? n : null
}

// Strip markdown emphasis / code markers and surrounding whitespace.
function cleanLabel(text) {
  return String(text || '').replace(/[*`]/g, '').trim()
}

function titleCase(word) {
  return word ? word[0].toUpperCase() + word.slice(1) : word
}

// Normalize a metric name for matching across rows
function metricKey(name) {
  return name.toLowerCase().replace(/s$/, '')
}

// columns: [{ name, values: [raw…] }] -> { labels, series } keeping only the
// columns that are numeric in every row.
function finalize(labels, columns) {
  const series = []
  for (const col of columns) {
    const nums = col.values.map(toNumber)
    if (nums.length >= MIN_ROWS && nums.every(v => v !== null)) {
      series.push({ name: titleCase(cleanLabel(col.name)), values: nums })
    }
  }

  if (!series.length || labels.length < MIN_ROWS) {
    return null
  }

  return { labels, series }
}

// Split a markdown table row "| a | b |" into trimmed cells.
function splitRow(line) {
  const cells = line.trim().split('|').map(s => s.trim())
  if (cells.length && cells[0] === '') {
    cells.shift()
  }

  if (cells.length && cells[cells.length - 1] === '') {
    cells.pop()
  }

  return cells
}

function extractFromTable(text) {
  const lines = text.split('\n')

  for (let i = 0; i < lines.length; i++) {
    const here = lines[i].trim()
    const next = (lines[i + 1] || '').trim()
    const isHeader = here.startsWith('|')
    const isDivider = next.startsWith('|') && /^[|\s:-]+$/.test(next) && next.includes('-')
    if (!isHeader || !isDivider) {
      continue
    }

    const tableLines = []
    let j = i
    while (j < lines.length && lines[j].trim().startsWith('|')) {
      tableLines.push(lines[j])
      j++
    }

    const header = splitRow(tableLines[0])
    const bodyRows = tableLines.slice(2).map(splitRow).filter(r => r.length)
    if (header.length < 2 || bodyRows.length < MIN_ROWS) {
      continue
    }

    const labels = bodyRows.map(r => cleanLabel(r[0]))
    const columns = header.slice(1).map((name, c) => ({
      name,
      values: bodyRows.map(r => r[c + 1])
    }))

    const result = finalize(labels, columns)
    if (result) {
      return result
    }
  }

  return null
}

// Matches "$2,020.00 revenue", "-$0.08 profit", "9 calls" -> [value, name].
const METRIC_RE = /(-?\$?-?[\d,]+(?:\.\d+)?)\s+([A-Za-z]+)/g

function parseMetricLine(raw) {
  // Only treat lines with a "|" separator as metric rows. Prose rarely uses
  // pipes, which keeps sentences from being mistaken for data.
  if (!raw.includes('|')) {
    return null
  }

  const line = raw.trim().replace(/^\s*(?:[-*]|\d+\.)\s+/, '')
  if (line.startsWith('|')) {
    return null
  }

  METRIC_RE.lastIndex = 0
  const first = METRIC_RE.exec(line)
  if (!first) {
    return null
  }

  const label = cleanLabel(line.slice(0, first.index).replace(/[\s\-–—:|]+$/, ''))
  if (!label || /^total\b/i.test(label)) {
    return null
  }

  // Parse metrics from the value part only, with parentheticals dropped so
  // "(4 converted)" does not become a stray series.
  const metricsPart = line.slice(first.index).replace(/\([^)]*\)/g, ' ')
  const metrics = []
  METRIC_RE.lastIndex = 0
  let m
  while ((m = METRIC_RE.exec(metricsPart)) !== null) {
    const value = toNumber(m[1])
    if (value === null) {
      continue
    }

    const key = metricKey(m[2])
    if (!metrics.some(x => x.key === key)) {
      metrics.push({ key, name: titleCase(m[2].toLowerCase()), value })
    }
  }

  return metrics.length ? { label, metrics } : null
}

function extractFromList(text) {
  const rows = []
  for (const line of text.split('\n')) {
    const parsed = parseMetricLine(line)
    if (parsed) {
      rows.push(parsed)
    }
  }

  if (rows.length < MIN_ROWS) {
    return null
  }

  // Series come from the first row's metrics, in order, kept only when every
  // row carries the same metric.
  const labels = rows.map(r => r.label)
  const series = []
  for (const fm of rows[0].metrics) {
    if (!rows.every(r => r.metrics.some(x => x.key === fm.key))) {
      continue
    }

    series.push({
      name: fm.name,
      values: rows.map(r => r.metrics.find(x => x.key === fm.key).value)
    })
  }

  return series.length ? { labels, series } : null
}

export function extractChartData(text) {
  const input = (text || '').trim()
  if (!input) {
    return null
  }

  return extractFromTable(input) || extractFromList(input)
}
